// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: d85b16b9-7cd2-4c88-8113-fccd26f7ee2d

// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

// ---------------------------------------------------------------------------
// Infrastructure import + server-to-application mapping
// ---------------------------------------------------------------------------
// In a real cloud-migration engagement the client hands over two things that
// nobody has ever reconciled:
//
//   1. an RVTools export of the VMware estate (vInfo/vHost/vDisk/... , often
//      3,000-6,000 VMs), or an AWS EC2 / Azure VM CSV, and
//   2. an application list that says nothing about which servers run what.
//
// Working out which servers belong to which application is the single most
// laborious task in discovery, and it is never fully automatic. So this screen
// does three things:
//
//   TAB 1  Import infrastructure — read the workbook, auto-map the columns
//          (RVTools header names drift between versions, so the aliases are
//          deliberately generous), and land every VM as an ITComponent fact
//          sheet. Unmapped columns become cf_* custom fields: nothing is lost.
//
//   TAB 2  Map servers to applications — suggestions that always say WHY, so a
//          consultant can trust or reject each one in a second, plus first-class
//          manual assignment for the long tail that no heuristic will ever get.
//
//   TAB 3  Coverage — mapped vs orphaned, apps with no servers, and real
//          vCPU/RAM/storage roll-ups per application (which is what turns cost
//          modelling from a guess into arithmetic).
//
// The assignment itself is stored as a relation on the APPLICATION
// (relations.itComponents = [{ id }]), which is where the rest of the product
// already looks for it.
// ---------------------------------------------------------------------------

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Divider,
  FormControlLabel, Grid, IconButton, LinearProgress, MenuItem, Paper, Slider,
  Stack, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField,
  Tooltip, Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import * as XLSX from 'xlsx';
import { FIELD_TYPES } from './schema';
import { exportCsv } from './exports';
import store from './store';
import {
  normalizeKey, IDENTITY_STRATEGIES, strategyFor, planImport, collisionReport,
  identityStamp, fingerprint, findRunByFingerprint, recordRun, listRuns,
} from './importIdentity';
import { detectTagColumns, parseRowTags, summarizeTags, mergeTags } from './importTags';
import HelpBubble from './HelpBubble';
import { TabLabel } from './ui';

const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEAD = 'sectionHeader.main';
const headSx = { '& th': { fontWeight: 700, color: HEAD } };

// A real estate is 4,000+ VMs. We never paint more than this many rows; every
// selection, bulk action and roll-up still applies to the full matching set.
const ROW_CAP = 300;
// The dry-run plan is over the WHOLE file (7,305 rows in the largest loaded
// estate). The counts are exact; only the rendered examples are capped.
const PREVIEW_CAP = 200;

const norm = (s) => String(s === undefined || s === null ? '' : s).toLowerCase().replace(/[^a-z0-9]/g, '');
const cell = (v) => String(v === undefined || v === null ? '' : v).trim();
const round1 = (n) => Math.round(n * 10) / 10;

// A cell that is a number, optionally wearing a currency symbol, thousands
// separators, an exponent and/or a trailing unit ("1,234.56 GB", "8 vCPU",
// "85%", "1.2e3"). Anchored on purpose: this parser also sees columns a user
// mapped by hand, so an OS string ("Windows Server 2019") or a cluster name
// ("prod-cluster-02") must come back as null rather than as stray digits.
const NUM_TEXT_RE = /^(?:[$€£¥]\s*)?(\d[\d,]*(?:\.\d+)?|\.\d+)(?:\s*[eE]([+-]?\d+))?\s*(?:%|[A-Za-z/µ]{1,8})?$/;

// Accounting notation: infrastructure and vendor exports write a negative as
// "(1,234.56)", not "-1,234.56". Stripping the parentheses without negating
// does not lose the sign, it INVERTS it — the value comes back positive and is
// wrong by twice itself. The parenthesis branch is load-bearing; keep it.
function parseNum(v) {
  if (v === undefined || v === null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  let s = String(v).trim();
  if (!s) return null;
  let neg = false;
  const wrapped = /^\((.*)\)$/.exec(s);
  if (wrapped) { neg = true; s = wrapped[1].trim(); }
  if (s[0] === '+' || s[0] === '-') { if (s[0] === '-') neg = !neg; s = s.slice(1).trim(); }
  const m = NUM_TEXT_RE.exec(s);
  if (!m) return null;
  const n = parseFloat(m[1].replace(/,/g, '') + (m[2] ? `e${m[2]}` : ''));
  if (!Number.isFinite(n)) return null;
  if (n === 0) return 0;
  return neg ? -n : n;
}

// ---------------------------------------------------------------------------
// Source detection
// ---------------------------------------------------------------------------
// RVTools is identified by its sheet names, not by the file name — clients
// rename the file, they never rename the tabs.
const RVTOOLS_SHEETS = ['vinfo', 'vhost', 'vdisk', 'vnetwork', 'vcpu', 'vmemory'];

export function detectRvTools(sheetNames) {
  const have = (sheetNames || []).map(norm);
  const hits = RVTOOLS_SHEETS.filter((s) => have.includes(s));
  return { isRvTools: hits.length >= 2, hits, vInfo: (sheetNames || []).find((s) => norm(s) === 'vinfo') };
}

// AWS and Azure exports are told apart by columns only they carry.
export function detectCloudSource(headers) {
  const h = (headers || []).map(norm);
  const has = (k) => h.some((x) => x.includes(k));
  if (has('instanceid') || has('privateipaddress') || has('availabilityzone') || has('instancetype')) return 'AWS';
  if (has('resourcegroup') || has('vmsize') || has('subscriptionid') || has('osdisk')) return 'Azure';
  return 'AWS';
}

// ---------------------------------------------------------------------------
// Column auto-mapping
// ---------------------------------------------------------------------------
// Aliases are normalised (lowercase, alphanumerics only). RVTools renames
// columns between versions ("Memory" -> "Memory MiB", "In Use MiB" ->
// "In Use MB"), and AWS/Azure exports use entirely different words for the same
// thing, so every target carries the whole family.
const COLUMN_TARGETS = [
  {
    key: 'name', label: 'Name', hint: 'VM / instance name — identifies the server',
    aliases: ['vm', 'name', 'vmname', 'virtualmachine', 'machinename', 'servername', 'instancename', 'computername', 'hostname'],
  },
  {
    key: 'powerState', label: 'Power state',
    aliases: ['powerstate', 'power', 'state', 'powerstatus', 'instancestate', 'status', 'vmstate'],
  },
  {
    key: 'vcpu', label: 'vCPUs',
    aliases: ['cpus', 'cpu', 'vcpus', 'vcpu', 'numcpu', 'numcpus', 'cpucount', 'vcpucount', 'processors', 'corecount'],
  },
  {
    key: 'ramGb', label: 'RAM (GB)', unit: true,
    aliases: ['memory', 'memorymib', 'memorymb', 'memorygb', 'memorygib', 'memorysizemib', 'ram', 'rammb', 'ramgb', 'memorysize'],
  },
  {
    key: 'storageGb', label: 'Storage provisioned (GB)', unit: true,
    aliases: ['provisionedmib', 'provisionedmb', 'provisionedgb', 'provisioned', 'provisionedspace', 'capacitymib', 'totaldiskgb', 'diskgb', 'storagegb', 'disksizegb', 'totaldisks'],
  },
  {
    key: 'inUseGb', label: 'Storage in use (GB)', unit: true,
    aliases: ['inusemib', 'inusemb', 'inuse', 'inusegb', 'usedspace', 'consumedmib', 'usedgb'],
  },
  {
    key: 'os', label: 'Operating system',
    aliases: ['osaccordingtotheconfigurationfile', 'osaccordingtothevmwaretools', 'osaccordingto', 'guestos', 'guestfullname', 'os', 'operatingsystem', 'osname', 'imageosname', 'platform', 'osversion'],
  },
  {
    key: 'eolDate', label: 'End of Life',
    hint: 'Hardware/OS end-of-life date. Obsolescence.js reads this as the fallback when no end-of-support date is recorded.',
    aliases: ['endoflife', 'eol', 'eoldate'],
  },
  {
    key: 'eosDate', label: 'End of Support',
    hint: 'Hardware/OS end-of-support date. Obsolescence.js reads this FIRST, ahead of anything inferred from the OS string.',
    aliases: ['endofsupport', 'eos', 'supportend', 'eosl'],
  },
  {
    key: 'extendedSupportEnd', label: 'Extended Support Ends',
    hint: 'Paid-extension end date (Microsoft ESU, Red Hat ELS, SUSE LTSS and similar). Surfaced as its own flag, never folded into the primary classification.',
    aliases: ['extendedsupport', 'extendedsupportend'],
  },
  {
    key: 'ipAddress', label: 'IP address',
    aliases: ['primaryipaddress', 'ipaddress', 'privateipaddress', 'privateip', 'primaryip', 'ip', 'ipv4', 'privateipaddresses'],
  },
  {
    key: 'dnsName', label: 'DNS name',
    aliases: ['dnsname', 'dns', 'fqdn', 'privatednsname', 'publicdnsname', 'domainname'],
  },
  {
    key: 'host', label: 'Host (hypervisor)',
    aliases: ['host', 'esxhost', 'esxihost', 'hypervisor', 'physicalhost', 'hostsystem'],
  },
  {
    key: 'cluster', label: 'Cluster',
    aliases: ['cluster', 'clustername', 'availabilityset', 'scaleset', 'vmssname'],
  },
  {
    key: 'datacenter', label: 'Datacenter',
    aliases: ['datacenter', 'datacentre', 'datacentername', 'region', 'location', 'availabilityzone', 'site'],
  },
  {
    key: 'folder', label: 'Folder / resource group',
    aliases: ['folder', 'resourcepool', 'resourcegroup', 'vmfolder', 'path'],
  },
  {
    key: 'annotation', label: 'Annotation / notes / tags',
    aliases: ['annotation', 'notes', 'note', 'tags', 'tag', 'comment', 'comments', 'description'],
  },
  {
    key: 'vmUuid', label: 'VM UUID / instance id',
    aliases: ['vmuuid', 'uuid', 'instanceuuid', 'smbiosuuid', 'instanceid', 'vmid', 'resourceid', 'objectid'],
  },
];

const TARGET_BY_KEY = new Map(COLUMN_TARGETS.map((t) => [t.key, t]));

// Two passes: exact alias match first (so "Memory" wins over "Memory
// Reservation"), then a contains-match for the leftovers. Aliases shorter than
// four characters never take part in the contains pass — "os" is a substring of
// "Host" and "ip" of "Description", and one bad guess in a 60-column RVTools
// sheet costs more than a blank.
export function autoMapColumns(headers) {
  const map = {};
  const usedHeader = new Set();
  const usedTarget = new Set();

  COLUMN_TARGETS.forEach((t) => {
    const hit = (headers || []).find((h) => !usedHeader.has(h) && t.aliases.indexOf(norm(h)) >= 0);
    if (hit) { map[hit] = t.key; usedHeader.add(hit); usedTarget.add(t.key); }
  });

  COLUMN_TARGETS.forEach((t) => {
    if (usedTarget.has(t.key)) return;
    const hit = (headers || []).find((h) => {
      if (usedHeader.has(h)) return false;
      const n = norm(h);
      return t.aliases.some((a) => a.length >= 4 && n.indexOf(a) >= 0);
    });
    if (hit) { map[hit] = t.key; usedHeader.add(hit); usedTarget.add(t.key); }
  });

  (headers || []).forEach((h) => { if (!map[h]) map[h] = ''; });
  return map;
}

// MiB is the RVTools default and GB is the cloud default, so guess from the
// header text and let the user override in the mapping table.
export function guessUnit(header) {
  const h = norm(header);
  if (h.indexOf('mib') >= 0 || h.indexOf('mb') >= 0) return 'MiB';
  if (h.indexOf('gib') >= 0 || h.indexOf('gb') >= 0) return 'GB';
  if (h.indexOf('memory') >= 0 || h.indexOf('provisioned') >= 0 || h.indexOf('inuse') >= 0) return 'MiB';
  return 'auto';
}

export function toGb(raw, unit) {
  const n = parseNum(raw);
  if (n === null) return null;
  const u = unit === 'auto' || !unit ? (n >= 512 ? 'MiB' : 'GB') : unit;
  return u === 'MiB' ? round1(n / 1024) : round1(n);
}

const DAY_MS = 86400000;
// Excel/Sheets store a date as a serial day count from 1899-12-30 (the epoch
// quirk that also encodes the fictitious 1900-02-29). A workbook column
// formatted as a date but read as a raw cell value comes through as that
// number ("45747") rather than text — exactly where a lifecycle-date column
// trips up an import that only expects text.
const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30);
// Serials for realistic lifecycle dates land well past 20000 (year 1954) and
// short of 60000 (year 2064); the bounds keep a bare "2024" or a small
// unrelated number typed into the wrong column from being read as a serial.
const EXCEL_SERIAL_MIN = 20000;
const EXCEL_SERIAL_MAX = 60000;

// Normalize a spreadsheet date-ish cell to canonical ISO (YYYY-MM-DD), or ''
// when it cannot be read as a date. Handles ISO already, Excel serial day
// numbers, and common text shapes (m/d/yyyy, d-mon-yyyy, ...) via Date.parse.
// Per CLAUDE.md's ingest rule this normalizes inline, once, at import time —
// downstream readers (Obsolescence.js's parseDate) get a canonical string
// rather than each having to re-guess the shape.
export function normalizeDateCell(raw) {
  const s = cell(raw);
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  // A bare number is EITHER an Excel serial OR garbage — never both, and
  // never handed to Date.parse below. Date.parse is dangerously lenient with
  // plain digit strings (engine-dependent, non-spec legacy fallback parsing):
  // "8" comes back as a real, plausible-looking ISO date instead of failing,
  // which is exactly the fabricated confidence this file exists to refuse.
  // A vCPU count or an unrelated small integer mapped to a date column by
  // mistake must come back empty, not a decades-off answer that looks legitimate.
  if (/^\d+(\.\d+)?$/.test(s)) {
    const n = parseNum(s);
    if (n !== null && n >= EXCEL_SERIAL_MIN && n <= EXCEL_SERIAL_MAX) {
      const d = new Date(EXCEL_EPOCH_MS + Math.round(n) * DAY_MS);
      return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
    }
    return '';
  }
  const t = Date.parse(s);
  return Number.isFinite(t) ? new Date(t).toISOString().slice(0, 10) : '';
}

// Targets whose values are dates and therefore run through normalizeDateCell
// rather than being stored as whatever text shape the source spreadsheet used.
const DATE_TARGET_KEYS = new Set(['eolDate', 'eosDate', 'extendedSupportEnd']);

const POWER_LABELS = {
  poweredon: 'Powered On', poweredoff: 'Powered Off', suspended: 'Suspended',
  running: 'Running', stopped: 'Stopped', deallocated: 'Deallocated',
};
const prettyPower = (v) => POWER_LABELS[norm(v)] || String(v || '').trim();

// The infra fields do not exist on the stock ITComponent schema. Registering
// them as (tenant-scoped) custom field defs is the sanctioned way to make them
// visible on the fact sheet detail without touching schema.js.
const INFRA_FIELD_DEFS = [
  { key: 'os', label: 'Operating System', type: FIELD_TYPES.TEXT },
  { key: 'vcpu', label: 'vCPUs', type: FIELD_TYPES.NUMBER },
  { key: 'ramGb', label: 'RAM (GB)', type: FIELD_TYPES.NUMBER },
  { key: 'storageGb', label: 'Storage Provisioned (GB)', type: FIELD_TYPES.NUMBER },
  { key: 'inUseGb', label: 'Storage In Use (GB)', type: FIELD_TYPES.NUMBER },
  { key: 'ipAddress', label: 'IP Address', type: FIELD_TYPES.TEXT },
  { key: 'dnsName', label: 'DNS Name', type: FIELD_TYPES.TEXT },
  { key: 'host', label: 'Host', type: FIELD_TYPES.TEXT },
  { key: 'cluster', label: 'Cluster', type: FIELD_TYPES.TEXT },
  { key: 'datacenter', label: 'Datacenter', type: FIELD_TYPES.TEXT },
  { key: 'folder', label: 'Folder / Resource Group', type: FIELD_TYPES.TEXT },
  { key: 'annotation', label: 'Annotation', type: FIELD_TYPES.TEXTAREA },
  { key: 'powerState', label: 'Power State', type: FIELD_TYPES.TEXT },
  { key: 'vmUuid', label: 'VM UUID', type: FIELD_TYPES.TEXT },
  { key: 'sourceSystem', label: 'Source System', type: FIELD_TYPES.TEXT },
];

// ---------------------------------------------------------------------------
// Row preparation — one function, used by BOTH the dry run and the writer
// ---------------------------------------------------------------------------
// The preview is only trustworthy if it is computed from exactly the values the
// writer will save. Two implementations would eventually drift, and the drift
// would show up as "12 updated" when nothing was written, or the reverse.
export function buildRowValues(row, { mapping, units, keepUnmapped, sourceSystem }) {
  const values = { name: '', sourceSystem, category: 'Server', lifecycleState: 'Active' };
  const headerKeys = Object.keys(mapping);
  for (let j = 0; j < headerKeys.length; j += 1) {
    const h = headerKeys[j];
    const target = mapping[h];
    const text = cell(row[h]);
    if (!text) continue;
    if (!target) {
      if (keepUnmapped && norm(h)) values[`cf_${norm(h)}`] = text;
    } else if (target === 'name') {
      values.name = text;
    } else if (target === 'vcpu') {
      const n = parseNum(text);
      if (n !== null) values.vcpu = Math.round(n);
    } else if (target === 'ramGb' || target === 'storageGb' || target === 'inUseGb') {
      const gb = toGb(text, units[h]);
      if (gb !== null) values[target] = gb;
    } else if (target === 'powerState') {
      values.powerState = prettyPower(text);
    } else if (DATE_TARGET_KEYS.has(target)) {
      const iso = normalizeDateCell(text);
      if (iso) values[target] = iso;
    } else {
      values[target] = text;
    }
  }
  return values;
}

// The identity stamp carries three things: the match key, the strategy that
// produced it, and the file it came from. Only the first two decide whether an
// otherwise-unchanged record has to be rewritten — re-saving 7,305 records
// because the client renamed the spreadsheet would be a write with nothing
// behind it.
const SOURCE_SENTINEL = '__primordia_import_source__';
const SAMPLE_STAMP = identityStamp({ name: 'x' }, 'name', SOURCE_SENTINEL);
const MATCH_STAMP_KEYS = Object.keys(SAMPLE_STAMP).filter((k) => SAMPLE_STAMP[k] !== SOURCE_SENTINEL);

const priorFields = (fs) => ({ ...(fs && fs.data), ...(fs && fs.fields) });

// The import is itself a lifecycle-date source, but stamping it must never
// clobber a curated attribution an admin already recorded on the fact sheet
// ("vendor matrix, verified 2026-08-15" is worth more than "import" and must
// survive a later re-import that merely refreshes vCPU/RAM). Stamp ONLY when
// this row actually supplied one of the three dates AND nothing already
// claims a source — not this row's own mapped value, and not the prior
// record's. `prior` is null for a brand-new fact sheet, where there is
// nothing to protect.
export function stampLifecycleSource(fields, prior) {
  if (fields.lifecycleSource) return fields;
  if (!(fields.eolDate || fields.eosDate || fields.extendedSupportEnd)) return fields;
  const priorFieldsObj = priorFields(prior);
  if (cell(priorFieldsObj.lifecycleSource)) return fields;
  return { ...fields, lifecycleSource: 'import' };
}

// Does this already-matched record still need its identity recorded? Only true
// the first time a record is stamped, or when the chosen strategy changes.
function needsStamp(prior, row, strategy, source) {
  const stamp = identityStamp(row, strategy, source);
  const before = priorFields(prior);
  return MATCH_STAMP_KEYS.some((k) => cell(before[k]) !== cell(stamp[k]));
}

const fieldLabel = (k) => {
  const t = TARGET_BY_KEY.get(k);
  if (t) return t.label;
  if (k.indexOf('cf_') === 0) return k.slice(3);
  return k;
};

const shortVal = (v) => {
  const s = v === undefined || v === null || v === '' ? '—' : String(v);
  return s.length > 30 ? `${s.slice(0, 29)}…` : s;
};

// ---------------------------------------------------------------------------
// Matching heuristics
// ---------------------------------------------------------------------------
// Hostnames in a hospital estate are built as <platform-prefix><app-token>
// <role><ordinal>: vmasairspl01, vmwsairsfw02, vmdbairs01 all point at
// "Airstrip One". Strip the prefix and the trailing ordinal and what is left
// starts with the application token.
const HOST_PREFIXES = [
  'vmas', 'vmdb', 'vmws', 'vmwd', 'vmsq', 'vmap', 'vmw', 'vms', 'vm',
  'srvr', 'srv', 'svr', 'prod', 'prd', 'test', 'tst', 'dev', 'uat', 'stg', 'qa',
  'win', 'lnx', 'lin', 'esx',
];
// Role suffixes: the letters that say what the box does rather than what it runs.
const HOST_SUFFIXES = ['sql', 'web', 'app', 'dbs', 'db', 'fw', 'pl', 'ws', 'svc', 'ap', 'mq', 'rp', 'dc', 'ct', 'px'];

const APP_NOISE = new Set([
  'the', 'a', 'an', 'and', 'of', 'for', 'system', 'systems', 'application',
  'applications', 'app', 'apps', 'service', 'services', 'solution', 'solutions',
  'platform', 'software', 'suite', 'inc', 'llc', 'ltd', 'corp', 'module',
  'server', 'servers', 'portal', 'enterprise',
]);

// Short name of an FQDN, punctuation removed.
export function hostKey(name) {
  return norm(String(name || '').split('.')[0]);
}

// The identifying part of a hostname: prefix stripped, trailing ordinal removed.
export function serverCore(name) {
  let s = hostKey(name);
  if (!s) return { core: '', trimmed: '' };
  const p = HOST_PREFIXES.find((x) => s.length > x.length + 2 && s.indexOf(x) === 0);
  if (p) s = s.slice(p.length);
  s = s.replace(/[0-9]+$/, '');
  let trimmed = s;
  const suf = HOST_SUFFIXES.find((x) => trimmed.length > x.length + 2 && trimmed.slice(-x.length) === x);
  if (suf) trimmed = trimmed.slice(0, trimmed.length - suf.length);
  return { core: s, trimmed };
}

export function appTokens(name) {
  const all = String(name || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);
  let tokens = all.filter((t) => !APP_NOISE.has(t));
  if (!tokens.length) tokens = all;
  return { tokens, flat: tokens.join('') };
}

// Prefix index over every application name + token, 3..10 characters. Turns the
// 4,000 x 500 comparison into a handful of map lookups per server.
export function buildAppNameIndex(apps) {
  const index = new Map();
  const contains = new Map(); // whole token (>=4 chars) -> app ids
  apps.forEach((app) => {
    const { tokens, flat } = appTokens(app.name);
    const keys = [flat].concat(tokens);
    keys.forEach((k) => {
      if (!k) return;
      const max = Math.min(k.length, 10);
      for (let len = 3; len <= max; len += 1) {
        const sub = k.slice(0, len);
        const bucket = index.get(sub) || [];
        bucket.push({ appId: app.id, appName: app.name, keyLen: k.length, whole: len === k.length });
        index.set(sub, bucket);
      }
    });
    tokens.forEach((t) => {
      if (t.length < 4) return;
      const bucket = contains.get(t) || new Set();
      bucket.add(app.id);
      contains.set(t, bucket);
    });
  });
  return { index, contains };
}

const PREFIX_BASE = { 3: 0.50, 4: 0.68, 5: 0.75, 6: 0.82, 7: 0.86 };
const baseScore = (len) => (len >= 8 ? 0.90 : PREFIX_BASE[len] || 0.45);

// Best name-pattern match for one server. `coreCounts` gives corroboration:
// three servers sharing a token is much better evidence than one.
export function matchByName(server, nameIndex, coreCounts, appById) {
  const { core, trimmed } = serverCore(server.name);
  const flat = hostKey(server.name);
  if (!core) return null;

  let best = null;
  const candidates = trimmed && trimmed !== core ? [core, trimmed] : [core];
  for (let ci = 0; ci < candidates.length; ci += 1) {
    const cand = candidates[ci];
    const max = Math.min(cand.length, 10);
    for (let len = max; len >= 3; len -= 1) {
      const sub = cand.slice(0, len);
      const entries = nameIndex.index.get(sub);
      if (!entries || !entries.length) continue;
      // Closest match = the app whose own key is shortest (least left over).
      const sorted = entries.slice().sort((a, b) => a.keyLen - b.keyLen);
      const top = sorted[0];
      const distinctApps = new Set(entries.map((e) => e.appId));
      let score = baseScore(len);
      if (top.whole) score += 0.05;
      if (len === cand.length) score += 0.03;
      if (distinctApps.size === 1) score += 0.08;
      else score -= 0.12;
      if ((coreCounts.get(cand) || 0) >= 2) score += 0.04;
      score = Math.max(0.3, Math.min(0.9, score));
      const others = [...distinctApps].filter((id) => id !== top.appId)
        .map((id) => (appById.get(id) || {}).name).filter(Boolean);
      const why = `"${sub}" in ${server.name} matches ${top.appName}`
        + ((coreCounts.get(cand) || 0) >= 2 ? ` (${coreCounts.get(cand)} servers share this token)` : '')
        + (others.length ? ` — also matches ${others.slice(0, 2).join(', ')}` : '');
      if (!best || score > best.confidence) best = { appId: top.appId, appName: top.appName, confidence: score, why };
      break; // longest prefix wins for this candidate
    }
  }

  // A whole application token sitting inside the hostname (vmepicprd01 -> Epic).
  nameIndex.contains.forEach((appIds, token) => {
    if (flat.indexOf(token) < 0) return;
    const ids = [...appIds];
    let score = 0.78 + Math.min(token.length, 9) * 0.01;
    if (ids.length > 1) score -= 0.12;
    score = Math.min(0.9, score);
    if (best && best.confidence >= score) return;
    const app = appById.get(ids[0]);
    if (!app) return;
    best = {
      appId: app.id,
      appName: app.name,
      confidence: score,
      why: `hostname ${server.name} contains the application word "${token}"`
        + (ids.length > 1 ? ` — shared with ${ids.length - 1} other application(s)` : ''),
    };
  });

  return best;
}

// ---- (b) declared server inventory ---------------------------------------
// Many application fact sheets already carry a free-text list of their servers
// (fields.serverInventory, or any custom field whose label mentions "server").
// A name the client typed themselves beats any heuristic we can invent, so this
// signal outranks name patterns.
const DECLARED_SKIP = new Set([
  'server', 'servers', 'none', 'n/a', 'na', 'unknown', 'tbd', 'and', 'the',
  'prod', 'production', 'test', 'dev', 'cluster', 'vmware', 'windows', 'linux',
]);

export function parseHostnames(text) {
  const out = [];
  String(text || '')
    .split(/[\s,;|/\\()[\]{}<>"'*]+/)
    .forEach((raw) => {
      const t = raw.replace(/[.:,;-]+$/, '').trim().toLowerCase();
      if (!t || t.length < 4 || t.length > 63) return;
      if (DECLARED_SKIP.has(t)) return;
      if (!/^[a-z0-9][a-z0-9._-]*$/.test(t)) return;
      if (/^[0-9.]+$/.test(t)) return;              // an IP, not a hostname
      if (!/[0-9]/.test(t) && t.indexOf('.') < 0) return; // prose word, not a host
      out.push(t);
    });
  return [...new Set(out)];
}

export function declaredServerText(app) {
  const fields = app.fields || {};
  const parts = [];
  Object.keys(fields).forEach((k) => {
    if (typeof fields[k] !== 'string') return;
    if (k === 'serverInventory' || /server/i.test(k)) parts.push(fields[k]);
  });
  return parts.join('\n');
}

export function buildDeclaredIndex(apps) {
  const index = new Map(); // host token -> Set(appId)
  apps.forEach((app) => {
    parseHostnames(declaredServerText(app)).forEach((h) => {
      const keys = [norm(h), hostKey(h)];
      keys.forEach((k) => {
        if (!k) return;
        const bucket = index.get(k) || new Set();
        bucket.add(app.id);
        index.set(k, bucket);
      });
    });
  });
  return index;
}

export function matchByDeclared(server, declaredIndex, appById) {
  const keys = [norm(server.name), hostKey(server.name)];
  const dns = (server.fields || {}).dnsName;
  if (dns) { keys.push(norm(dns)); keys.push(hostKey(dns)); }
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    if (!k) continue;
    const hit = declaredIndex.get(k);
    if (!hit || !hit.size) continue;
    const ids = [...hit];
    const app = appById.get(ids[0]);
    if (!app) continue;
    const conflicted = ids.length > 1;
    return {
      appId: app.id,
      appName: app.name,
      confidence: conflicted ? 0.70 : (i >= 2 ? 0.93 : 0.97),
      why: conflicted
        ? `${server.name} is listed in the declared server inventory of ${ids.length} applications`
        : `${server.name} is listed by name in ${app.name}'s declared server inventory`,
    };
  }
  return null;
}

// ---- (c) IP / subnet proximity -------------------------------------------
export const subnetOf = (ip) => {
  const m = String(ip || '').trim().match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\./);
  return m ? `${m[1]}.${m[2]}.${m[3]}.0/24` : '';
};

export function buildSubnetIndex(servers, serverToApp) {
  const index = new Map(); // subnet -> Map(appId -> count)
  servers.forEach((s) => {
    const appId = serverToApp.get(s.id);
    if (!appId) return;
    const net = subnetOf((s.fields || {}).ipAddress);
    if (!net) return;
    const bucket = index.get(net) || new Map();
    bucket.set(appId, (bucket.get(appId) || 0) + 1);
    index.set(net, bucket);
  });
  return index;
}

export function matchBySubnet(server, subnetIndex, appById) {
  const net = subnetOf((server.fields || {}).ipAddress);
  if (!net) return null;
  const bucket = subnetIndex.get(net);
  if (!bucket || !bucket.size) return null;
  const ranked = [...bucket.entries()].sort((a, b) => b[1] - a[1]);
  const app = appById.get(ranked[0][0]);
  if (!app) return null;
  const exclusive = ranked.length === 1;
  return {
    appId: app.id,
    appName: app.name,
    confidence: exclusive ? 0.60 : 0.42,
    why: exclusive
      ? `${server.fields.ipAddress} is in ${net}, where all ${ranked[0][1]} already-mapped server(s) belong to ${app.name}`
      : `${server.fields.ipAddress} is in ${net}, shared by ${ranked.length} applications (${app.name} has the most servers there)`,
  };
}

const SIGNAL_WEIGHT = { declared: 3, name: 2, subnet: 1 };
const SIGNAL_LABEL = { declared: 'Declared inventory', name: 'Name pattern', subnet: 'Subnet proximity' };
const SIGNAL_COLOR = { declared: '#2E7D32', name: BLUE, subnet: '#B26A00' };

// One suggestion per server: the strongest signal wins, and a declared
// hostname always beats a clever name guess.
export function computeSuggestions(unassigned, apps, allServers, serverToApp) {
  if (!unassigned.length || !apps.length) return [];
  const appById = new Map(apps.map((a) => [a.id, a]));
  const nameIndex = buildAppNameIndex(apps);
  const declaredIndex = buildDeclaredIndex(apps);
  const subnetIndex = buildSubnetIndex(allServers, serverToApp);

  const coreCounts = new Map();
  unassigned.forEach((s) => {
    const { core, trimmed } = serverCore(s.name);
    if (core) coreCounts.set(core, (coreCounts.get(core) || 0) + 1);
    if (trimmed && trimmed !== core) coreCounts.set(trimmed, (coreCounts.get(trimmed) || 0) + 1);
  });

  const out = [];
  for (let i = 0; i < unassigned.length; i += 1) {
    const server = unassigned[i];
    const cands = [];
    const d = matchByDeclared(server, declaredIndex, appById);
    if (d) cands.push({ ...d, signal: 'declared' });
    const n = matchByName(server, nameIndex, coreCounts, appById);
    if (n) cands.push({ ...n, signal: 'name' });
    const p = matchBySubnet(server, subnetIndex, appById);
    if (p) cands.push({ ...p, signal: 'subnet' });
    if (!cands.length) continue;
    cands.sort((a, b) => (SIGNAL_WEIGHT[b.signal] - SIGNAL_WEIGHT[a.signal]) || (b.confidence - a.confidence));
    const best = cands[0];
    out.push({
      key: `${server.id}::${best.appId}`,
      serverId: server.id,
      serverName: server.name,
      appId: best.appId,
      appName: best.appName,
      signal: best.signal,
      confidence: best.confidence,
      why: best.why,
    });
  }
  out.sort((a, b) => b.confidence - a.confidence || a.serverName.localeCompare(b.serverName));
  return out;
}

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------
function Kpi({ label, value, sub, color = HEAD }) {
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="overline" sx={{ color: GREY }}>{label}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, color }}>{value}</Typography>
      {sub ? <Typography variant="body2" sx={{ color: GREY }}>{sub}</Typography> : null}
    </Paper>
  );
}

// One outcome of the dry run. The count is the whole file; the list is capped
// so 7,305 rows do not put 7,305 nodes on the page.
function PlanList({ title, count, color, note, open, onToggle, children }) {
  return (
    <Paper sx={{ p: 1.5, mb: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton size="small" onClick={onToggle} disabled={!count}>
          {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 700, color, minWidth: 64 }}>{count}</Typography>
        <Box>
          <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
          <Typography variant="caption" sx={{ color: GREY }}>{note}</Typography>
        </Box>
      </Stack>
      {open && count ? (
        <Box sx={{ mt: 1, maxHeight: 300, overflow: 'auto', borderTop: '1px solid #eceff1', pt: 1 }}>
          {children}
        </Box>
      ) : null}
    </Paper>
  );
}

function ConfidenceBar({ value }) {
  const pct = Math.round(value * 100);
  const color = pct >= 90 ? '#2E7D32' : pct >= 75 ? BLUE : '#B26A00';
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 110 }}>
      <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: '#eceff1' }}>
        <Box sx={{ width: `${pct}%`, height: 6, borderRadius: 3, bgcolor: color }} />
      </Box>
      <Typography variant="caption" sx={{ fontWeight: 700, color }}>{pct}%</Typography>
    </Stack>
  );
}

const specOf = (s) => {
  const f = s.fields || {};
  return {
    vcpu: parseNum(f.vcpu) || 0,
    ram: parseNum(f.ramGb) || 0,
    storage: parseNum(f.storageGb) || 0,
  };
};

// ===========================================================================
// Component
// ===========================================================================
export default function InfrastructureImport({ tenantId }) {
  const [tab, setTab] = useState(0);
  const [apps, setApps] = useState([]);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');

  // ---- Tab 1 state --------------------------------------------------------
  const fileRef = useRef(null);
  const [workbook, setWorkbook] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [sheet, setSheet] = useState('');
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [units, setUnits] = useState({});
  const [sourceSystem, setSourceSystem] = useState('RVTools');
  const [rvTools, setRvTools] = useState(false);
  const [keepUnmapped, setKeepUnmapped] = useState(true);
  // Source-cloud tags (Azure/AWS/vSphere tag columns) parsed into real
  // Primordia tags at import time. Additive: the raw columns still land as
  // annotation / cf_* text exactly as before. Off = exactly today's behavior.
  const [importSourceTags, setImportSourceTags] = useState(true);
  const [importErr, setImportErr] = useState('');
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importResult, setImportResult] = useState(null);

  // ---- Tab 1: identity, dry run, run history ------------------------------
  const [strategy, setStrategy] = useState('name');
  const [collisionAck, setCollisionAck] = useState(false);
  const [fileHash, setFileHash] = useState('');
  const [priorRun, setPriorRun] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planning, setPlanning] = useState(false);
  const [openList, setOpenList] = useState('');
  const [runs, setRuns] = useState([]);

  // ---- Tab 2 state --------------------------------------------------------
  const [serverSearch, setServerSearch] = useState('');
  const [appSearch, setAppSearch] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [rejected, setRejected] = useState(() => new Set());
  const [targetApp, setTargetApp] = useState(null);
  const [expandedApp, setExpandedApp] = useState('');
  const [threshold, setThreshold] = useState(0.75);
  const [busy, setBusy] = useState(false);
  const [mapNote, setMapNote] = useState('');
  const anchorRef = useRef(-1);

  const reload = () => {
    setLoading(true);
    setLoadErr('');
    Promise.all([
      store.listFactSheets(tenantId, { type: 'Application' }),
      store.listFactSheets(tenantId, { type: 'ITComponent' }),
    ])
      .then(([a, s]) => { setApps(a || []); setServers(s || []); })
      .catch(() => setLoadErr('Could not load this workspace. Reload the page and try again.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setSelected(new Set());
    setRejected(new Set());
    setTargetApp(null);
    setRuns(listRuns(tenantId));
    reload();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [tenantId]);

  // ---- Derived: who is mapped to what -------------------------------------
  const serverById = useMemo(() => new Map(servers.map((s) => [s.id, s])), [servers]);

  const { serverToApp, appToServers } = useMemo(() => {
    const s2a = new Map();
    const a2s = new Map();
    apps.forEach((app) => {
      const rel = ((app.relations || {}).itComponents) || [];
      const ids = rel.map((r) => (r && r.id) || '').filter(Boolean);
      a2s.set(app.id, ids);
      ids.forEach((id) => { if (!s2a.has(id)) s2a.set(id, app.id); });
    });
    return { serverToApp: s2a, appToServers: a2s };
  }, [apps]);

  const appById = useMemo(() => new Map(apps.map((a) => [a.id, a])), [apps]);

  const unassigned = useMemo(
    () => servers.filter((s) => !serverToApp.has(s.id)),
    [servers, serverToApp]
  );

  const suggestions = useMemo(() => {
    if (tab !== 1 || loading) return [];
    return computeSuggestions(unassigned, apps, servers, serverToApp);
  }, [tab, loading, unassigned, apps, servers, serverToApp]);

  const liveSuggestions = useMemo(
    () => suggestions.filter((s) => !rejected.has(s.key)),
    [suggestions, rejected]
  );

  // =========================================================================
  // TAB 1 — import
  // =========================================================================
  const readSheet = (wb, sheetName, forcedSource) => {
    const ws = wb.Sheets[sheetName];
    if (!ws) return;
    const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
    // Header row = the first row with the most non-empty cells (RVTools and
    // client-massaged exports both like a title row or two on top).
    let hIdx = 0;
    let best = 0;
    for (let i = 0; i < Math.min(raw.length, 15); i += 1) {
      const c = (raw[i] || []).filter((x) => String(x).trim()).length;
      if (c > best) { best = c; hIdx = i; }
    }
    const hdrs = (raw[hIdx] || []).map((h) => String(h).trim()).filter(Boolean);
    const data = XLSX.utils.sheet_to_json(ws, { range: hIdx, defval: '' });
    const map = autoMapColumns(hdrs);
    const u = {};
    hdrs.forEach((h) => { if (TARGET_BY_KEY.get(map[h]) && TARGET_BY_KEY.get(map[h]).unit) u[h] = guessUnit(h); });
    setHeaders(hdrs);
    setRows(data.filter((r) => Object.values(r).some((v) => String(v).trim())));
    setMapping(map);
    setUnits(u);
    setSheet(sheetName);
    if (forcedSource) setSourceSystem(forcedSource);
    else if (!rvTools) setSourceSystem(detectCloudSource(hdrs));
  };

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setImportErr('');
    setImportResult(null);
    setPlan(null);
    setCollisionAck(false);
    setStrategy('name');
    setFileHash('');
    setPriorRun(null);
    try {
      const buf = await file.arrayBuffer();
      // Hash the bytes before anything else: "RVTools_export (2).xlsx" and the
      // file loaded last week are the same file, whatever they are called.
      const hash = await fingerprint(buf);
      setFileHash(hash);
      setPriorRun(findRunByFingerprint(tenantId, hash));
      const wb = XLSX.read(buf, { type: 'array' });
      const det = detectRvTools(wb.SheetNames);
      setWorkbook(wb);
      setFileName(file.name);
      setSheetNames(wb.SheetNames);
      setRvTools(det.isRvTools);
      const first = det.isRvTools ? (det.vInfo || wb.SheetNames[0]) : wb.SheetNames[0];
      readSheet(wb, first, det.isRvTools ? 'RVTools' : null);
    } catch (e2) {
      setImportErr('Could not read that file. Supported: .xlsx, .xls, .csv');
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const resetFile = () => {
    setWorkbook(null); setSheetNames([]); setSheet(''); setFileName('');
    setHeaders([]); setRows([]); setMapping({}); setUnits({});
    setRvTools(false); setImportErr(''); setImportResult(null); setProgress(0);
    setPlan(null); setCollisionAck(false); setStrategy('name');
    setFileHash(''); setPriorRun(null); setOpenList('');
  };

  const nameHeader = useMemo(
    () => Object.keys(mapping).find((h) => mapping[h] === 'name') || '',
    [mapping]
  );
  const mappedCount = useMemo(() => Object.values(mapping).filter(Boolean).length, [mapping]);

  // ---- Source-cloud tag columns -------------------------------------------
  // Detection is over the header list; the summary is computed over EVERY row
  // (counts are exact, only the rendered group list is capped at 8).
  const tagCols = useMemo(() => detectTagColumns(headers), [headers]);
  const tagSummary = useMemo(
    () => ((tagCols.columns.length || tagCols.reserved.length) ? summarizeTags(rows, tagCols) : null),
    [rows, tagCols]
  );

  // ---- Identity ------------------------------------------------------------
  // Only the three identity columns, so the collision report can be recomputed
  // as the user tries each strategy without re-parsing 7,305 x 60 cells.
  const identityRows = useMemo(() => {
    const nameH = Object.keys(mapping).find((h) => mapping[h] === 'name') || '';
    const dnsH = Object.keys(mapping).find((h) => mapping[h] === 'dnsName') || '';
    const idH = Object.keys(mapping).find((h) => mapping[h] === 'vmUuid') || '';
    return rows.map((r) => ({
      name: nameH ? cell(r[nameH]) : '',
      dnsName: dnsH ? cell(r[dnsH]) : '',
      sourceId: idH ? cell(r[idH]) : '',
    }));
  }, [rows, mapping]);

  // How many rows each strategy can actually key on. A strategy that is blank
  // everywhere is not an option, it is a silent fallback to name matching.
  const strategyStats = useMemo(() => IDENTITY_STRATEGIES.map((s) => {
    const get = strategyFor(s.key).get;
    let filled = 0;
    for (let i = 0; i < identityRows.length; i += 1) if (get(identityRows[i])) filled += 1;
    return { ...s, filled };
  }), [identityRows]);

  const statFor = (key) => strategyStats.find((s) => s.key === key) || { filled: 0 };
  const sourceIdFilled = statFor('sourceId').filled;

  const collisions = useMemo(
    () => collisionReport(identityRows, strategy),
    [identityRows, strategy]
  );

  const blocked = collisions.collisions.length > 0 && !collisionAck;

  const chooseStrategy = (key) => {
    setStrategy(key);
    setCollisionAck(false);
    setPlan(null);
  };

  // Any change to how the file is read invalidates the plan. A stale preview is
  // worse than no preview: it is a promise about writes that will not happen.
  useEffect(() => {
    setPlan(null);
    setOpenList('');
  }, [rows, mapping, units, keepUnmapped, sourceSystem]);

  // A collision acknowledgement is about a specific set of collisions. Change
  // the sheet or the identity columns and it has to be earned again.
  useEffect(() => { setCollisionAck(false); }, [identityRows]);

  // ---- Dry run -------------------------------------------------------------
  // Nothing is written here. One pass builds the values, one pass matches them
  // against what is already in the workspace, and the user sees the result
  // before a single fact sheet is touched.
  const buildPlan = async () => {
    if (!nameHeader) { setImportErr('Map one column to "Name" — it is what identifies each server.'); return; }
    setImportErr('');
    setImportResult(null);
    setPlan(null);
    setPlanning(true);
    await new Promise((r) => setTimeout(r, 0)); // let the spinner paint first

    let existing = [];
    try {
      existing = await store.listFactSheets(tenantId, { type: 'ITComponent' });
    } catch (err) {
      setPlanning(false);
      setImportErr('Could not read the existing servers for this workspace — nothing was previewed and nothing was written.');
      return;
    }

    const opts = { mapping, units, keepUnmapped, sourceSystem };
    const prepared = [];
    const noName = [];
    for (let i = 0; i < rows.length; i += 1) {
      const values = buildRowValues(rows[i], opts);
      if (!values.name) {
        noName.push({ i, name: '(blank)', reason: `the "${nameHeader}" cell is empty — a server with no name cannot be created` });
        continue;
      }
      prepared.push({
        i,
        name: values.name,
        dnsName: values.dnsName || '',
        sourceId: values.vmUuid || '',
        values,
      });
    }

    const p = planImport(prepared, existing, { strategy });

    // An unchanged row still has to carry its identity key, or the NEXT import
    // has nothing to match on. That is a write, so it is counted separately
    // rather than being passed off as a field update.
    const unchanged = [];
    const stampOnly = [];
    p.unchanged.forEach((e) => {
      if (needsStamp(e.prior, e.row, strategy, fileName)) stampOnly.push(e);
      else unchanged.push(e);
    });

    setPlan({
      strategy,
      create: p.create,
      update: p.update,
      unchanged,
      stampOnly,
      // e.i is the position within the prepared list; e.row.i is the row in the
      // file, which is the only number the user can look up in the spreadsheet.
      skipped: noName.concat(p.skipped.map((e) => ({ i: e.row.i, name: e.row.name || '(blank)', reason: e.reason }))),
      existingCount: existing.length,
      rowCount: rows.length,
    });
    setPlanning(false);
  };

  const runImport = async () => {
    if (!plan) return;
    setImportErr('');
    setImporting(true);
    setProgress(0);
    setImportResult(null);

    // Register field defs once, so the imported values are visible (and
    // editable) on the ITComponent fact sheet rather than invisible payload.
    try {
      const defs = await store.listFieldDefs(tenantId, 'ITComponent');
      const have = new Set(defs.map((d) => d.key));
      for (let i = 0; i < INFRA_FIELD_DEFS.length; i += 1) {
        const def = INFRA_FIELD_DEFS[i];
        if (!have.has(def.key)) { await store.addFieldDef(tenantId, 'ITComponent', def); have.add(def.key); }
      }
      if (keepUnmapped) {
        const unmappedHeaders = headers.filter((h) => !mapping[h]);
        for (let i = 0; i < unmappedHeaders.length; i += 1) {
          const h = unmappedHeaders[i];
          const key = `cf_${norm(h)}`;
          if (have.has(key) || !norm(h)) continue;
          await store.addFieldDef(tenantId, 'ITComponent', { key, label: h, type: FIELD_TYPES.TEXT });
          have.add(key);
        }
      }
    } catch (err) { /* field defs are cosmetic — never block the import */ }

    // Exactly the writes the plan promised, in the order it listed them.
    let created = 0;
    let updated = 0;
    let stamped = 0;
    let failed = 0;
    const errors = [];
    const total = plan.create.length + plan.update.length + plan.stampOnly.length;
    let done = 0;
    const tick = async () => {
      done += 1;
      if (done % 20 === 0 || done === total) {
        setProgress(Math.round((done / Math.max(total, 1)) * 100));
        await new Promise((r) => setTimeout(r, 0)); // let the progress bar paint
      }
    };
    const fail = (name, err) => {
      failed += 1;
      if (errors.length < 5) errors.push(`${name}: ${(err && err.message) || 'save failed'}`);
    };

    for (let i = 0; i < plan.create.length; i += 1) {
      const e = plan.create[i];
      const fields = stampLifecycleSource(
        { ...e.row.values, ...identityStamp(e.row, plan.strategy, fileName) }, null
      );
      // e.row.i is the row's position in the file — the same `rows` the plan
      // was computed from, so the tags written are the tags previewed.
      const rowTags = importSourceTags ? parseRowTags(rows[e.row.i], tagCols) : [];
      try {
        const fs = await store.createFactSheet(tenantId, 'ITComponent', e.row.name);
        await store.saveFactSheet({
          ...fs,
          fields: { ...(fs.fields || {}), ...fields },
          tags: mergeTags(fs.tags, rowTags),
        });
        created += 1;
      } catch (err) { fail(e.row.name, err); }
      await tick();
    }

    for (let i = 0; i < plan.update.length; i += 1) {
      const e = plan.update[i];
      const fields = stampLifecycleSource(
        { ...e.row.values, ...identityStamp(e.row, plan.strategy, fileName) }, e.prior
      );
      // Merged, never replaced: tags somebody set by hand on the existing
      // record survive a re-import. mergeTags copies — e.prior's own tags
      // array may be frozen store state and is never mutated.
      const rowTags = importSourceTags ? parseRowTags(rows[e.row.i], tagCols) : [];
      try {
        // The display name follows the file: matching on a recorded key is what
        // makes a rename survivable, and a record that keeps the old name after
        // the client renamed the VM is just a different kind of wrong.
        await store.saveFactSheet({
          ...e.prior,
          name: e.row.name,
          tags: mergeTags(e.prior.tags, rowTags),
          fields: { ...(e.prior.fields || {}), ...fields },
        });
        updated += 1;
      } catch (err) { fail(e.row.name, err); }
      await tick();
    }

    for (let i = 0; i < plan.stampOnly.length; i += 1) {
      const e = plan.stampOnly[i];
      try {
        await store.saveFactSheet({
          ...e.prior,
          fields: { ...(e.prior.fields || {}), ...identityStamp(e.row, plan.strategy, fileName) },
        });
        stamped += 1;
      } catch (err) { fail(e.row.name, err); }
      await tick();
    }

    const counts = {
      created,
      updated,
      stamped,
      unchanged: plan.unchanged.length,
      skipped: plan.skipped.length,
      failed,
      total: plan.rowCount,
    };

    setProgress(100);
    setImporting(false);
    setImportResult({ ...counts, errors });
    recordRun(tenantId, {
      file: fileName,
      sheet,
      fingerprint: fileHash,
      strategy: plan.strategy,
      counts,
    });
    setRuns(listRuns(tenantId));
    setPriorRun(findRunByFingerprint(tenantId, fileHash));
    setPlan(null);
    setOpenList('');
    reload();
  };

  // =========================================================================
  // TAB 2 — assignment
  // =========================================================================
  const persistAssignment = async (appId, nextIds) => {
    const app = appById.get(appId);
    if (!app) return;
    await store.saveFactSheet({
      ...app,
      relations: { ...(app.relations || {}), itComponents: nextIds.map((id) => ({ id })) },
    });
  };

  // Grouped by application so a bulk-accept of 400 suggestions is a handful of
  // writes, not 400 read-modify-write races on the same fact sheet.
  const assignMany = async (pairs) => {
    if (!pairs.length) return;
    setBusy(true);
    setMapNote('');
    const byApp = new Map();
    pairs.forEach((p) => {
      const list = byApp.get(p.appId) || [];
      list.push(p.serverId);
      byApp.set(p.appId, list);
    });
    let ok = 0;
    let failed = 0;
    const appIds = [...byApp.keys()];
    for (let i = 0; i < appIds.length; i += 1) {
      const appId = appIds[i];
      const current = (appToServers.get(appId) || []).slice();
      const seen = new Set(current);
      const adding = byApp.get(appId);
      for (let j = 0; j < adding.length; j += 1) {
        if (!seen.has(adding[j])) { current.push(adding[j]); seen.add(adding[j]); }
      }
      try {
        await persistAssignment(appId, current);
        ok += adding.length;
      } catch (err) {
        failed += adding.length;
      }
    }
    setSelected(new Set());
    setBusy(false);
    setMapNote(failed
      ? `Assigned ${ok} server(s); ${failed} could not be saved.`
      : `Assigned ${ok} server(s) to ${appIds.length} application(s).`);
    reload();
  };

  const unassignServers = async (appId, serverIds) => {
    const drop = new Set(serverIds);
    const next = (appToServers.get(appId) || []).filter((id) => !drop.has(id));
    setBusy(true);
    setMapNote('');
    try {
      await persistAssignment(appId, next);
      setMapNote(`Removed ${serverIds.length} server(s) from ${(appById.get(appId) || {}).name || 'the application'}.`);
    } catch (err) {
      setMapNote('That change could not be saved.');
    }
    setBusy(false);
    reload();
  };

  const filteredUnassigned = useMemo(() => {
    const q = normalizeKey(serverSearch);
    if (!q) return unassigned;
    return unassigned.filter((s) => {
      const f = s.fields || {};
      return normalizeKey(s.name).indexOf(q) >= 0
        || normalizeKey(f.ipAddress).indexOf(q) >= 0
        || normalizeKey(f.os).indexOf(q) >= 0
        || normalizeKey(f.cluster).indexOf(q) >= 0;
    });
  }, [unassigned, serverSearch]);

  const visibleUnassigned = filteredUnassigned.slice(0, ROW_CAP);
  const allFilteredSelected = filteredUnassigned.length > 0 && filteredUnassigned.every((s) => selected.has(s.id));

  // Checkbox + shift-click range over the FILTERED list (which is what the user
  // can see and reason about), even though only the first ROW_CAP are painted.
  const toggleServer = (index, shift) => {
    const next = new Set(selected);
    const row = filteredUnassigned[index];
    if (!row) return;
    if (shift && anchorRef.current >= 0 && anchorRef.current < filteredUnassigned.length) {
      const from = Math.min(anchorRef.current, index);
      const to = Math.max(anchorRef.current, index);
      const turnOn = !next.has(row.id);
      for (let i = from; i <= to; i += 1) {
        const r = filteredUnassigned[i];
        if (!r) continue;
        if (turnOn) next.add(r.id); else next.delete(r.id);
      }
    } else if (next.has(row.id)) {
      next.delete(row.id);
    } else {
      next.add(row.id);
    }
    anchorRef.current = index;
    setSelected(next);
  };

  const toggleAllFiltered = () => {
    const next = new Set(selected);
    if (allFilteredSelected) filteredUnassigned.forEach((s) => next.delete(s.id));
    else filteredUnassigned.forEach((s) => next.add(s.id));
    anchorRef.current = -1;
    setSelected(next);
  };

  const appRows = useMemo(() => {
    const q = normalizeKey(appSearch);
    return apps
      .map((a) => {
        const ids = (appToServers.get(a.id) || []).filter((id) => serverById.has(id));
        return { app: a, serverIds: ids, count: ids.length };
      })
      .filter((r) => !q || normalizeKey(r.app.name).indexOf(q) >= 0)
      .sort((a, b) => b.count - a.count || a.app.name.localeCompare(b.app.name));
  }, [apps, appToServers, serverById, appSearch]);

  const highConfidence = useMemo(
    () => liveSuggestions.filter((s) => s.confidence >= threshold),
    [liveSuggestions, threshold]
  );

  // =========================================================================
  // TAB 3 — coverage
  // =========================================================================
  const coverage = useMemo(() => {
    const mappedIds = new Set();
    appToServers.forEach((ids) => ids.forEach((id) => { if (serverById.has(id)) mappedIds.add(id); }));
    const orphans = servers.filter((s) => !mappedIds.has(s.id));
    const perApp = apps.map((a) => {
      const ids = (appToServers.get(a.id) || []).filter((id) => serverById.has(id));
      let vcpu = 0;
      let ram = 0;
      let storage = 0;
      ids.forEach((id) => {
        const spec = specOf(serverById.get(id));
        vcpu += spec.vcpu; ram += spec.ram; storage += spec.storage;
      });
      return { app: a, count: ids.length, vcpu, ram: round1(ram), storage: round1(storage) };
    });
    const totals = servers.reduce((acc, s) => {
      const spec = specOf(s);
      acc.vcpu += spec.vcpu; acc.ram += spec.ram; acc.storage += spec.storage;
      return acc;
    }, { vcpu: 0, ram: 0, storage: 0 });
    const orphanTotals = orphans.reduce((acc, s) => {
      const spec = specOf(s);
      acc.vcpu += spec.vcpu; acc.ram += spec.ram; acc.storage += spec.storage;
      return acc;
    }, { vcpu: 0, ram: 0, storage: 0 });
    return {
      mapped: mappedIds.size,
      orphans,
      perApp: perApp.sort((a, b) => b.vcpu - a.vcpu || b.count - a.count || a.app.name.localeCompare(b.app.name)),
      zeroApps: perApp.filter((r) => r.count === 0).map((r) => r.app),
      totals: { vcpu: totals.vcpu, ram: round1(totals.ram), storage: round1(totals.storage) },
      orphanTotals: { vcpu: orphanTotals.vcpu, ram: round1(orphanTotals.ram), storage: round1(orphanTotals.storage) },
    };
  }, [apps, servers, appToServers, serverById]);

  const exportMapping = () => {
    const rowsOut = [];
    apps.forEach((a) => {
      (appToServers.get(a.id) || []).forEach((id) => {
        const s = serverById.get(id);
        if (s) rowsOut.push({ app: a.name, s });
      });
    });
    servers.forEach((s) => { if (!serverToApp.has(s.id)) rowsOut.push({ app: '', s }); });
    exportCsv(`server-application-mapping-${tenantId}.csv`, rowsOut, [
      { label: 'Server', value: (r) => r.s.name },
      { label: 'Application', value: (r) => r.app || '(unassigned)' },
      { label: 'Source', value: (r) => (r.s.fields || {}).sourceSystem || '' },
      { label: 'Operating system', value: (r) => (r.s.fields || {}).os || '' },
      { label: 'vCPU', value: (r) => (r.s.fields || {}).vcpu || '' },
      { label: 'RAM (GB)', value: (r) => (r.s.fields || {}).ramGb || '' },
      { label: 'Storage (GB)', value: (r) => (r.s.fields || {}).storageGb || '' },
      { label: 'IP address', value: (r) => (r.s.fields || {}).ipAddress || '' },
      { label: 'Cluster', value: (r) => (r.s.fields || {}).cluster || '' },
      { label: 'Datacenter', value: (r) => (r.s.fields || {}).datacenter || '' },
      { label: 'Power state', value: (r) => (r.s.fields || {}).powerState || '' },
    ]);
  };

  const exportOrphans = () => {
    exportCsv(`orphaned-servers-${tenantId}.csv`, coverage.orphans, [
      { label: 'Server', value: (s) => s.name },
      { label: 'Operating system', value: (s) => (s.fields || {}).os || '' },
      { label: 'vCPU', value: (s) => (s.fields || {}).vcpu || '' },
      { label: 'RAM (GB)', value: (s) => (s.fields || {}).ramGb || '' },
      { label: 'Storage (GB)', value: (s) => (s.fields || {}).storageGb || '' },
      { label: 'IP address', value: (s) => (s.fields || {}).ipAddress || '' },
      { label: 'DNS name', value: (s) => (s.fields || {}).dnsName || '' },
      { label: 'Cluster', value: (s) => (s.fields || {}).cluster || '' },
      { label: 'Datacenter', value: (s) => (s.fields || {}).datacenter || '' },
      { label: 'Power state', value: (s) => (s.fields || {}).powerState || '' },
      { label: 'Source', value: (s) => (s.fields || {}).sourceSystem || '' },
    ]);
  };

  const exportAppRollup = () => {
    exportCsv(`application-infrastructure-rollup-${tenantId}.csv`, coverage.perApp, [
      { label: 'Application', value: (r) => r.app.name },
      { label: 'Servers', value: (r) => r.count },
      { label: 'vCPU', value: (r) => r.vcpu },
      { label: 'RAM (GB)', value: (r) => r.ram },
      { label: 'Storage (GB)', value: (r) => r.storage },
    ]);
  };

  // =========================================================================
  // Render
  // =========================================================================
  const priorCounts = (priorRun && priorRun.counts) || {};
  const capNote = (len) => (len > PREVIEW_CAP ? (
    <Typography variant="caption" sx={{ color: GREY, display: 'block', pt: 0.5 }}>
      Showing the first {PREVIEW_CAP} of {len} — the count above is the whole file.
    </Typography>
  ) : null);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: HEAD }}>Infrastructure</Typography>
        <HelpBubble topic="infrastructureImport" />
        <Chip size="small" label={`workspace: ${tenantId}`} variant="outlined" />
        {loading ? <CircularProgress size={18} /> : (
          <Chip size="small" label={`${servers.length} servers · ${apps.length} applications`} sx={{ bgcolor: 'chipMuted.main', color: HEAD, fontWeight: 600 }} />
        )}
      </Stack>

      {loadErr ? <Alert severity="error" sx={{ mb: 2 }}>{loadErr}</Alert> : null}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Tab label={<TabLabel text="Import infrastructure" topic="tabImportInfrastructure" />} sx={{ fontWeight: 700, textTransform: 'none' }} />
        <Tab label={<TabLabel text="Map servers to applications" topic="tabMapServersToApps" />} sx={{ fontWeight: 700, textTransform: 'none' }} />
        <Tab label={<TabLabel text="Coverage" topic="tabInfraCoverage" />} sx={{ fontWeight: 700, textTransform: 'none' }} />
      </Tabs>

      {/* ============================ TAB 1 ============================ */}
      {tab === 0 ? (
        <Box>
          {importErr ? <Alert severity="error" sx={{ mb: 2 }}>{importErr}</Alert> : null}
          {importResult ? (
            <Alert severity={importResult.errors.length ? 'warning' : 'success'} sx={{ mb: 2 }}>
              {importResult.created} server(s) created, {importResult.updated} updated,{' '}
              {importResult.unchanged} already identical
              {importResult.stamped ? `, ${importResult.stamped} had their identity key recorded` : ''}
              {importResult.skipped ? `, ${importResult.skipped} skipped` : ''}
              {importResult.failed ? `, ${importResult.failed} failed to save` : ''} out of{' '}
              {importResult.total} rows.
              {importResult.errors.length ? ` First problems: ${importResult.errors.join('; ')}` : ''}
            </Alert>
          ) : null}

          {!rows.length ? (
            <Box
              onClick={() => fileRef.current && fileRef.current.click()}
              sx={{
                border: '2px dashed #cfd8dc', borderRadius: 2, p: 5, textAlign: 'center',
                cursor: 'pointer', '&:hover': { borderColor: BLUE, bgcolor: '#f7fbff' },
              }}
            >
              <UploadFileIcon sx={{ fontSize: 44, color: BLUE }} />
              <Typography sx={{ fontWeight: 600, mt: 1 }}>Choose an infrastructure export</Typography>
              <Typography variant="body2" sx={{ color: GREY }}>
                .xlsx or .csv — an RVTools workbook (vInfo, vHost, vDisk, vNetwork, vCPU, vMemory),
                an AWS EC2 inventory, or an Azure VM export
              </Typography>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={onFile} />
            </Box>
          ) : (
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap' }}>
                <Chip label={fileName} onDelete={importing ? undefined : resetFile} />
                {rvTools ? (
                  <Chip color="primary" label="RVTools workbook detected" sx={{ fontWeight: 600 }} />
                ) : (
                  <Chip label="Generic cloud export" variant="outlined" />
                )}
                <Chip label={`${rows.length} rows`} />
                <Chip label={`${mappedCount}/${headers.length} columns mapped`} />
                {sheetNames.length > 1 ? (
                  <TextField
                    select size="small" label="Sheet" value={sheet}
                    onChange={(e) => readSheet(workbook, e.target.value)} sx={{ minWidth: 160 }}
                  >
                    {sheetNames.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </TextField>
                ) : null}
                <TextField
                  select size="small" label="Source system" value={sourceSystem}
                  onChange={(e) => setSourceSystem(e.target.value)} sx={{ minWidth: 150 }}
                >
                  <MenuItem value="RVTools">RVTools</MenuItem>
                  <MenuItem value="AWS">AWS</MenuItem>
                  <MenuItem value="Azure">Azure</MenuItem>
                </TextField>
              </Stack>

              {priorRun ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <b>This exact file has already been imported into this workspace</b> on{' '}
                  {new Date(priorRun.at).toLocaleString()}, matched on{' '}
                  {strategyFor(priorRun.strategy).label.toLowerCase()} — {priorCounts.created || 0} created,{' '}
                  {priorCounts.updated || 0} updated, {priorCounts.unchanged || 0} unchanged. Importing it
                  again will not duplicate anything, but if you meant to load a newer export, this is not it.
                </Alert>
              ) : null}

              <Alert severity="info" sx={{ mb: 2 }}>
                Every row becomes an <b>IT Component</b> fact sheet of subtype Server. Existing records
                are matched on the identity you choose below and updated in place, so re-importing a
                refreshed export never doubles the estate — and you see exactly what will happen before
                anything is written.
              </Alert>

              {/* ---- identity ---- */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD }}>
                  Record identity
                </Typography>
                <Typography variant="body2" sx={{ color: GREY, mb: 1.5 }}>
                  What makes a row in this file the same server as one already in this workspace? The
                  answer is stamped onto every record this import writes, so the next export from the
                  same source matches the same way — even after a VM has been renamed.
                </Typography>

                <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                  <TextField
                    select size="small" label="Match rows on" value={strategy}
                    onChange={(e) => chooseStrategy(e.target.value)} sx={{ minWidth: 300 }}
                  >
                    {strategyStats.map((s) => (
                      <MenuItem key={s.key} value={s.key} disabled={!s.filled && s.key !== strategy}>
                        {s.label}
                        {s.filled ? ` — ${s.filled} of ${rows.length} rows` : ' — not present in this file'}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Typography variant="caption" sx={{ color: GREY, maxWidth: 560 }}>
                    {strategyFor(strategy).help}
                  </Typography>
                </Stack>

                {sourceIdFilled && strategy !== 'sourceId' ? (
                  <Alert
                    severity="info" sx={{ mt: 1.5 }}
                    action={<Button size="small" onClick={() => chooseStrategy('sourceId')}>Use it</Button>}
                  >
                    This file carries a VM UUID on {sourceIdFilled} of {rows.length} rows.{' '}
                    <b>Recommended:</b> match on the source system ID — it is the only key here that
                    survives a VM being renamed in vCenter.
                  </Alert>
                ) : null}

                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, mt: 1.5 }}>
                  <Chip size="small" label={`${collisions.distinct} distinct keys`} sx={{ bgcolor: 'chipMuted.main', color: HEAD, fontWeight: 600 }} />
                  <Chip size="small" variant="outlined" label={`${collisions.blank} rows with no key`} />
                  <Chip
                    size="small" label={`${collisions.collisions.length} collisions`}
                    sx={{
                      fontWeight: 700,
                      bgcolor: collisions.collisions.length ? '#fdecea' : '#e8f5e9',
                      color: collisions.collisions.length ? '#B71C1C' : '#2E7D32',
                    }}
                  />
                </Stack>

                {collisions.collisions.length ? (
                  <Alert severity="error" icon={<WarningAmberIcon />} sx={{ mt: 1.5 }}>
                    <b>
                      {collisions.collisions.length} pair(s) of rows with different names share the same
                      key under this strategy.
                    </b>{' '}
                    Only the first row of each pair is imported — the others are skipped and those
                    servers never land in the workspace. Silently merging two distinct machines is worse
                    than creating a duplicate, because nothing on screen tells you a server went missing.
                    <Box component="ul" sx={{ pl: 2.5, mt: 1, mb: 0 }}>
                      {collisions.collisions.slice(0, 10).map((c) => (
                        <li key={`${c.key}-${c.a}-${c.b}`}>
                          <code>{c.key}</code> — <b>{c.a}</b> vs <b>{c.b}</b>
                        </li>
                      ))}
                    </Box>
                    {collisions.collisions.length > 10 ? (
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                        …and {collisions.collisions.length - 10} more.
                      </Typography>
                    ) : null}
                    <FormControlLabel
                      sx={{ mt: 1 }}
                      control={(
                        <Checkbox
                          size="small" checked={collisionAck}
                          onChange={(e) => setCollisionAck(e.target.checked)}
                        />
                      )}
                      label={(
                        <span style={{ fontSize: 13 }}>
                          I have checked these and accept that each pair really is one server.
                        </span>
                      )}
                    />
                  </Alert>
                ) : (
                  <Typography variant="caption" sx={{ color: '#2E7D32', display: 'block', mt: 1 }}>
                    No two rows with different names share a key under this strategy.
                  </Typography>
                )}

                {collisions.blank ? (
                  <Typography variant="caption" sx={{ color: '#B26A00', display: 'block', mt: 0.5 }}>
                    {collisions.blank} row(s) have no value for this key. They fall back to matching on
                    their name; any row with no name at all is skipped and listed in the plan.
                  </Typography>
                ) : null}
              </Paper>

              <FormControlLabel
                control={<Checkbox size="small" checked={keepUnmapped} onChange={(e) => setKeepUnmapped(e.target.checked)} />}
                label={<span style={{ fontSize: 13 }}>Keep unmapped columns as cf_* custom fields (nothing is dropped)</span>}
              />
              <Box>
                <FormControlLabel
                  control={<Checkbox size="small" checked={importSourceTags} onChange={(e) => setImportSourceTags(e.target.checked)} />}
                  label={(
                    <span style={{ fontSize: 13 }}>
                      Import source tags as tags — recognized cloud/vSphere tag columns become real
                      Primordia tags on each server; the raw columns still import exactly as before
                    </span>
                  )}
                />
              </Box>

              {tagSummary ? (
                <Paper sx={{ p: 2, my: 1.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD }}>
                    Tags detected
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, mt: 0.5 }}>
                    {tagSummary.columns.map((c) => (
                      <Chip
                        key={c.header} size="small" variant="outlined"
                        label={c.kind === 'packed' ? `${c.header} — packed key:value pairs` : `${c.header} → group “${c.group}”`}
                      />
                    ))}
                  </Stack>
                  {tagSummary.reserved.length ? (
                    <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 0.5 }}>
                      Ignored as AWS system tags (reserved “aws:” prefix):{' '}
                      {tagSummary.reserved.map((r) => r.header).join(', ')}.
                    </Typography>
                  ) : null}
                  {tagSummary.distinctPairs > 0 ? (
                    <>
                      <Typography variant="body2" sx={{ color: GREY, mt: 1 }}>
                        {tagSummary.rowsWithTags} of {tagSummary.rowCount} row(s) carry tags —{' '}
                        {tagSummary.distinctGroups} distinct group(s), {tagSummary.distinctPairs} distinct
                        (group, value) pair(s).
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, mt: 1 }}>
                        {tagSummary.groups.slice(0, 8).map((g) => (
                          <Chip
                            key={g.group} size="small"
                            sx={{ bgcolor: 'chipMuted.main', color: HEAD, fontWeight: 600 }}
                            label={`${g.group} — ${g.rows} row(s), ${g.values} value(s)`}
                          />
                        ))}
                        {tagSummary.groups.length > 8 ? (
                          <Typography variant="caption" sx={{ color: GREY, alignSelf: 'center' }}>
                            …and {tagSummary.groups.length - 8} more group(s) — every group is imported,
                            only this list is capped.
                          </Typography>
                        ) : null}
                      </Stack>
                    </>
                  ) : null}
                  {(tagSummary.truncatedGroups || tagSummary.truncatedValues) ? (
                    <Typography variant="caption" sx={{ color: '#B26A00', display: 'block', mt: 0.5 }}>
                      Length caps applied: {tagSummary.truncatedGroups} group name(s) truncated to 40
                      characters, {tagSummary.truncatedValues} value(s) to 120.
                    </Typography>
                  ) : null}
                  {tagSummary.reservedSkipped ? (
                    <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 0.5 }}>
                      {tagSummary.reservedSkipped} pair(s) with the reserved “aws:” key prefix were
                      skipped inside packed cells.
                    </Typography>
                  ) : null}
                  {tagSummary.unparsedPacked.map((s) => (
                    <Alert key={s.header} severity="info" sx={{ mt: 1 }}>
                      Column “{s.header}” was found but no key:value pairs were recognized in its{' '}
                      {s.nonEmptyCells} non-empty cell(s) — the values are kept as text on whatever
                      field the column maps to, nothing becomes a tag.
                    </Alert>
                  ))}
                  <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 1 }}>
                    {importSourceTags
                      ? 'Tags are written onto created and updated servers, merged with any tags a record already carries. Rows that are already identical are not rewritten just to add tags.'
                      : 'Off — this import behaves exactly as before: these columns land as text only, no tags are written.'}
                  </Typography>
                </Paper>
              ) : null}

              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow sx={headSx}>
                    <TableCell>Column in file</TableCell>
                    <TableCell>Sample value</TableCell>
                    <TableCell>Maps to</TableCell>
                    <TableCell>Unit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {headers.map((h) => {
                    const target = TARGET_BY_KEY.get(mapping[h]);
                    return (
                      <TableRow key={h} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{h}</TableCell>
                        <TableCell sx={{ color: GREY, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {String((rows[0] || {})[h] === undefined ? '' : (rows[0] || {})[h])}
                        </TableCell>
                        <TableCell sx={{ minWidth: 220 }}>
                          <TextField
                            select size="small" fullWidth value={mapping[h] || ''}
                            onChange={(e) => {
                              const next = { ...mapping, [h]: e.target.value };
                              setMapping(next);
                              const t = TARGET_BY_KEY.get(e.target.value);
                              if (t && t.unit && !units[h]) setUnits({ ...units, [h]: guessUnit(h) });
                            }}
                          >
                            <MenuItem value=""><em>{keepUnmapped ? 'custom field' : 'skip'}</em></MenuItem>
                            {COLUMN_TARGETS.map((t) => (
                              <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell sx={{ width: 120 }}>
                          {target && target.unit ? (
                            <TextField
                              select size="small" fullWidth value={units[h] || 'auto'}
                              onChange={(e) => setUnits({ ...units, [h]: e.target.value })}
                            >
                              <MenuItem value="auto">auto</MenuItem>
                              <MenuItem value="MiB">MiB</MenuItem>
                              <MenuItem value="GB">GB</MenuItem>
                            </TextField>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {planning ? <LinearProgress sx={{ mt: 2 }} /> : null}
              {importing ? <LinearProgress variant="determinate" value={progress} sx={{ mt: 2 }} /> : null}

              {plan ? (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD }}>
                    Dry run — nothing has been written yet
                  </Typography>
                  <Typography variant="body2" sx={{ color: GREY, mb: 1 }}>
                    {plan.rowCount} row(s) in this file, matched against the {plan.existingCount} IT
                    Component(s) already in this workspace on{' '}
                    {strategyFor(plan.strategy).label.toLowerCase()}. The counts below cover every row;
                    each list shows the first {PREVIEW_CAP}.
                  </Typography>

                  <PlanList
                    title="new servers to create" count={plan.create.length} color={BLUE}
                    note="nothing in the workspace matched these rows"
                    open={openList === 'create'} onToggle={() => setOpenList(openList === 'create' ? '' : 'create')}
                  >
                    {plan.create.slice(0, PREVIEW_CAP).map((e) => (
                      <Typography key={`c${e.i}`} variant="body2" sx={{ py: 0.2 }}>
                        {e.row.name}
                        {e.key ? <span style={{ color: GREY }}>{` · key ${e.key}`}</span> : null}
                      </Typography>
                    ))}
                    {capNote(plan.create.length)}
                  </PlanList>

                  <PlanList
                    title="existing servers to update" count={plan.update.length} color="#B26A00"
                    note="matched an existing record, and at least one field differs"
                    open={openList === 'update'} onToggle={() => setOpenList(openList === 'update' ? '' : 'update')}
                  >
                    {plan.update.slice(0, PREVIEW_CAP).map((e) => {
                      const before = priorFields(e.prior);
                      const renamed = normalizeKey(e.prior.name) !== normalizeKey(e.row.name);
                      return (
                        <Box key={`u${e.i}`} sx={{ py: 0.4, borderBottom: '1px solid #f5f5f5' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {e.row.name}
                            {renamed ? (
                              <span style={{ color: '#B26A00', fontWeight: 400 }}>
                                {` — renamed from "${e.prior.name}"`}
                              </span>
                            ) : null}
                          </Typography>
                          <Typography variant="caption" sx={{ color: GREY }}>
                            matched by {e.matchedBy} ·{' '}
                            {e.changed.slice(0, 6)
                              .map((k) => `${fieldLabel(k)} ${shortVal(before[k])} → ${shortVal(e.row.values[k])}`)
                              .join('  ·  ')}
                            {e.changed.length > 6 ? ` · +${e.changed.length - 6} more field(s)` : ''}
                          </Typography>
                        </Box>
                      );
                    })}
                    {capNote(plan.update.length)}
                  </PlanList>

                  <PlanList
                    title="already identical — left alone" count={plan.unchanged.length} color="#2E7D32"
                    note="matched, and every mapped value is the same as what is stored"
                    open={openList === 'unchanged'} onToggle={() => setOpenList(openList === 'unchanged' ? '' : 'unchanged')}
                  >
                    {plan.unchanged.slice(0, PREVIEW_CAP).map((e) => (
                      <Typography key={`n${e.i}`} variant="body2" sx={{ py: 0.2 }}>
                        {e.row.name} <span style={{ color: GREY }}>{`· matched by ${e.matchedBy}`}</span>
                      </Typography>
                    ))}
                    {capNote(plan.unchanged.length)}
                  </PlanList>

                  <PlanList
                    title="identity key to be recorded" count={plan.stampOnly.length} color={HEAD}
                    note="no field changes — only the match key, so the next import finds them"
                    open={openList === 'stamp'} onToggle={() => setOpenList(openList === 'stamp' ? '' : 'stamp')}
                  >
                    {plan.stampOnly.slice(0, PREVIEW_CAP).map((e) => (
                      <Typography key={`s${e.i}`} variant="body2" sx={{ py: 0.2 }}>
                        {e.row.name} <span style={{ color: GREY }}>{`· matched by ${e.matchedBy}`}</span>
                      </Typography>
                    ))}
                    {capNote(plan.stampOnly.length)}
                  </PlanList>

                  <PlanList
                    title="rows to be skipped" count={plan.skipped.length} color="#B71C1C"
                    note="these rows will not be imported — every one of them is listed"
                    open={openList === 'skipped'} onToggle={() => setOpenList(openList === 'skipped' ? '' : 'skipped')}
                  >
                    {plan.skipped.slice(0, PREVIEW_CAP).map((e) => (
                      <Typography key={`k${e.i}`} variant="body2" sx={{ py: 0.2 }}>
                        <b>{`row ${e.i + 1}: ${e.name}`}</b> <span style={{ color: GREY }}>{`— ${e.reason}`}</span>
                      </Typography>
                    ))}
                    {capNote(plan.skipped.length)}
                  </PlanList>
                </Box>
              ) : null}

              <Stack direction="row" spacing={2} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
                {!plan ? (
                  <Button
                    variant="contained" onClick={buildPlan} disabled={planning || importing || blocked}
                    sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
                  >
                    {planning ? 'Working out what would change…' : `Preview what importing ${rows.length} rows would do`}
                  </Button>
                ) : (
                  <Button
                    variant="contained" onClick={runImport} disabled={importing || blocked}
                    sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
                  >
                    {importing
                      ? `Importing ${progress}%`
                      : `Import — create ${plan.create.length}, update ${plan.update.length}`}
                  </Button>
                )}
                {plan && !importing ? (
                  <Button onClick={() => { setPlan(null); setOpenList(''); }}>Cancel — change something first</Button>
                ) : null}
                <Button onClick={resetFile} disabled={importing || planning}>Choose a different file</Button>
              </Stack>

              {blocked ? (
                <Typography variant="caption" sx={{ color: '#B71C1C', display: 'block', mt: 1 }}>
                  Acknowledge the collisions above, or choose a different identity, before previewing.
                </Typography>
              ) : null}
            </Box>
          )}

          {/* ---- run history ---- */}
          {runs.length ? (
            <Paper sx={{ p: 2, mt: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD }}>
                Import history
              </Typography>
              <Typography variant="body2" sx={{ color: GREY, mb: 1 }}>
                Every import into this workspace, newest first — so &quot;did we already load the second
                vCenter export?&quot; has an answer.
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow sx={headSx}>
                    <TableCell>File</TableCell>
                    <TableCell>Imported</TableCell>
                    <TableCell>Matched on</TableCell>
                    <TableCell align="right">Created</TableCell>
                    <TableCell align="right">Updated</TableCell>
                    <TableCell align="right">Unchanged</TableCell>
                    <TableCell align="right">Skipped</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runs.slice(0, 20).map((r, i) => {
                    const c = r.counts || {};
                    return (
                      <TableRow key={`${r.at}-${i}`} hover selected={!!fileHash && r.fingerprint === fileHash}>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {r.file}
                          {r.sheet ? <span style={{ color: GREY, fontWeight: 400 }}>{` · ${r.sheet}`}</span> : null}
                        </TableCell>
                        <TableCell sx={{ color: GREY }}>{new Date(r.at).toLocaleString()}</TableCell>
                        <TableCell>{strategyFor(r.strategy).label}</TableCell>
                        <TableCell align="right">{c.created || 0}</TableCell>
                        <TableCell align="right">{c.updated || 0}</TableCell>
                        <TableCell align="right">{c.unchanged || 0}</TableCell>
                        <TableCell align="right" sx={{ color: c.skipped ? '#B71C1C' : 'inherit' }}>{c.skipped || 0}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          ) : null}
        </Box>
      ) : null}

      {/* ============================ TAB 2 ============================ */}
      {tab === 1 ? (
        <Box>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}>
              <Kpi label="Unassigned servers" value={unassigned.length} color={unassigned.length ? '#B26A00' : '#2E7D32'}
                sub={`${servers.length - unassigned.length} of ${servers.length} mapped`} />
            </Grid>
            <Grid item xs={6} md={3}>
              <Kpi label="Applications" value={apps.length} sub={`${appRows.filter((r) => r.count > 0).length} have servers`} />
            </Grid>
            <Grid item xs={6} md={3}>
              <Kpi label="Suggestions" value={liveSuggestions.length} color={BLUE} sub="each one says why" />
            </Grid>
            <Grid item xs={6} md={3}>
              <Kpi label="Selected" value={selected.size} color={HEAD} sub="ready to assign manually" />
            </Grid>
          </Grid>

          {mapNote ? <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMapNote('')}>{mapNote}</Alert> : null}

          {/* ---- suggestions ---- */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD }}>
                Suggested assignments
              </Typography>
              <Chip size="small" label={`${liveSuggestions.length} suggestions`} />
              <Box sx={{ flex: 1 }} />
              <Typography variant="caption" sx={{ color: GREY }}>High-confidence cut-off</Typography>
              <Slider
                size="small" value={threshold} min={0.5} max={1} step={0.05}
                onChange={(e, v) => setThreshold(Array.isArray(v) ? v[0] : v)}
                valueLabelDisplay="auto" valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
                sx={{ width: 160 }}
              />
              <Button
                variant="contained" size="small" disabled={busy || !highConfidence.length}
                onClick={() => assignMany(highConfidence)}
                sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
              >
                Accept all {highConfidence.length} at {Math.round(threshold * 100)}%+
              </Button>
            </Stack>
            <Typography variant="body2" sx={{ color: GREY, mb: 1 }}>
              A declared server inventory on the application fact sheet is the strongest signal — it is
              the client&apos;s own answer. Name patterns come next (prefixes like vm/vmas/vmdb and trailing
              ordinals are ignored), then IP proximity to servers already mapped to that application.
            </Typography>

            {busy ? <LinearProgress sx={{ mb: 1 }} /> : null}

            {!liveSuggestions.length ? (
              <Alert severity="info">
                No suggestions right now. Either everything is mapped, or the application fact sheets
                carry nothing to match on yet — fill in a server inventory field on a few applications
                and they will appear here.
              </Alert>
            ) : (
              <Box sx={{ maxHeight: 380, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={headSx}>
                      <TableCell>Server</TableCell>
                      <TableCell>Proposed application</TableCell>
                      <TableCell>Signal</TableCell>
                      <TableCell>Why</TableCell>
                      <TableCell>Confidence</TableCell>
                      <TableCell align="right">Decide</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {liveSuggestions.slice(0, ROW_CAP).map((s) => (
                      <TableRow key={s.key} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{s.serverName}</TableCell>
                        <TableCell>{s.appName}</TableCell>
                        <TableCell>
                          <Chip
                            size="small" label={SIGNAL_LABEL[s.signal]}
                            sx={{ bgcolor: '#fff', border: `1px solid ${SIGNAL_COLOR[s.signal]}`, color: SIGNAL_COLOR[s.signal], fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: GREY, maxWidth: 360 }}>{s.why}</TableCell>
                        <TableCell><ConfidenceBar value={s.confidence} /></TableCell>
                        <TableCell align="right">
                          <Tooltip title="Accept — link this server to this application">
                            <span>
                              <IconButton size="small" color="primary" disabled={busy}
                                onClick={() => assignMany([{ serverId: s.serverId, appId: s.appId }])}>
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Reject — hide this suggestion">
                            <span>
                              <IconButton size="small" disabled={busy}
                                onClick={() => {
                                  const next = new Set(rejected);
                                  next.add(s.key);
                                  setRejected(next);
                                }}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {liveSuggestions.length > ROW_CAP ? (
                  <Typography variant="caption" sx={{ color: GREY, p: 1, display: 'block' }}>
                    Showing the {ROW_CAP} highest-confidence suggestions of {liveSuggestions.length}.
                    Bulk accept still applies to every suggestion above the cut-off.
                  </Typography>
                ) : null}
              </Box>
            )}
          </Paper>

          {/* ---- manual panes ---- */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD }}>Unassigned servers</Typography>
                  <Chip size="small" color={unassigned.length ? 'warning' : 'success'} label={unassigned.length} />
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
                  <TextField
                    size="small" label="Search name, IP, OS, cluster" value={serverSearch}
                    onChange={(e) => { setServerSearch(e.target.value); anchorRef.current = -1; }}
                    sx={{ flex: 1, minWidth: 200 }}
                  />
                  <Autocomplete
                    size="small" options={apps} value={targetApp}
                    getOptionLabel={(o) => (o && o.name) || ''}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    onChange={(e, v) => setTargetApp(v)}
                    renderInput={(params) => <TextField {...params} label="Assign to application" />}
                    sx={{ minWidth: 240 }}
                  />
                  <Button
                    variant="contained" disabled={busy || !targetApp || !selected.size}
                    onClick={() => assignMany([...selected].map((id) => ({ serverId: id, appId: targetApp.id })))}
                    sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
                  >
                    Assign {selected.size || ''}
                  </Button>
                </Stack>
                <Typography variant="caption" sx={{ color: GREY }}>
                  Tick to select, shift-click to select a range. Selection covers all{' '}
                  {filteredUnassigned.length} matching servers, not just the rows on screen.
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={headSx}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small" checked={allFilteredSelected}
                            indeterminate={!allFilteredSelected && filteredUnassigned.some((s) => selected.has(s.id))}
                            onChange={toggleAllFiltered}
                          />
                        </TableCell>
                        <TableCell>Server</TableCell>
                        <TableCell>OS</TableCell>
                        <TableCell align="right">vCPU</TableCell>
                        <TableCell align="right">RAM</TableCell>
                        <TableCell>IP</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleUnassigned.map((s, i) => {
                        const f = s.fields || {};
                        return (
                          <TableRow key={s.id} hover selected={selected.has(s.id)}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small" checked={selected.has(s.id)}
                                onChange={(e) => toggleServer(i, !!(e.nativeEvent && e.nativeEvent.shiftKey))}
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{s.name}</TableCell>
                            <TableCell sx={{ color: GREY, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.os || ''}</TableCell>
                            <TableCell align="right">{f.vcpu || ''}</TableCell>
                            <TableCell align="right">{f.ramGb || ''}</TableCell>
                            <TableCell sx={{ color: GREY }}>{f.ipAddress || ''}</TableCell>
                          </TableRow>
                        );
                      })}
                      {!filteredUnassigned.length ? (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ color: GREY }}>
                            Nothing unassigned{serverSearch ? ' matches that search' : ' — every server belongs to an application'}.
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                  {filteredUnassigned.length > ROW_CAP ? (
                    <Typography variant="caption" sx={{ color: GREY, p: 1, display: 'block' }}>
                      Showing {ROW_CAP} of {filteredUnassigned.length} matching servers — narrow the search,
                      or use &quot;select all&quot; above to act on the whole set.
                    </Typography>
                  ) : null}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD }}>Applications</Typography>
                  <Chip size="small" label={`${apps.length}`} />
                  <Chip size="small" variant="outlined" label={`${appRows.filter((r) => !r.count).length} with no servers`} />
                </Stack>
                <TextField
                  size="small" fullWidth label="Search applications" value={appSearch}
                  onChange={(e) => setAppSearch(e.target.value)} sx={{ mb: 1 }}
                />
                <Divider sx={{ my: 1 }} />
                <Box sx={{ maxHeight: 470, overflow: 'auto' }}>
                  {appRows.slice(0, ROW_CAP).map((r) => (
                    <Box key={r.app.id} sx={{ borderBottom: '1px solid #eceff1', py: 0.5 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <IconButton size="small" onClick={() => setExpandedApp(expandedApp === r.app.id ? '' : r.app.id)}>
                          {expandedApp === r.app.id ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                        </IconButton>
                        <Typography sx={{ fontWeight: 600, flex: 1 }}>{r.app.name}</Typography>
                        <Chip
                          size="small" label={`${r.count} server${r.count === 1 ? '' : 's'}`}
                          sx={{ bgcolor: r.count ? '#eef3f9' : '#fdecea', color: r.count ? HEAD : '#B71C1C', fontWeight: 600 }}
                        />
                        <Tooltip title="Assign the selected servers to this application">
                          <span>
                            <Button
                              size="small" disabled={busy || !selected.size}
                              onClick={() => assignMany([...selected].map((id) => ({ serverId: id, appId: r.app.id })))}
                            >
                              Assign here
                            </Button>
                          </span>
                        </Tooltip>
                      </Stack>
                      {expandedApp === r.app.id ? (
                        <Box sx={{ pl: 5, pb: 1 }}>
                          {!r.count ? (
                            <Typography variant="body2" sx={{ color: GREY }}>No servers mapped yet.</Typography>
                          ) : (
                            r.serverIds.slice(0, 100).map((id) => {
                              const s = serverById.get(id);
                              const f = (s && s.fields) || {};
                              return (
                                <Stack key={id} direction="row" alignItems="center" spacing={1}>
                                  <Typography variant="body2" sx={{ flex: 1 }}>
                                    {(s && s.name) || id}
                                    <span style={{ color: GREY }}>
                                      {f.vcpu ? ` · ${f.vcpu} vCPU` : ''}{f.ramGb ? ` · ${f.ramGb} GB` : ''}{f.ipAddress ? ` · ${f.ipAddress}` : ''}
                                    </span>
                                  </Typography>
                                  <Tooltip title="Un-assign this server">
                                    <span>
                                      <IconButton size="small" disabled={busy} onClick={() => unassignServers(r.app.id, [id])}>
                                        <LinkOffIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Stack>
                              );
                            })
                          )}
                          {r.count > 100 ? (
                            <Typography variant="caption" sx={{ color: GREY }}>
                              Showing 100 of {r.count} servers.
                            </Typography>
                          ) : null}
                        </Box>
                      ) : null}
                    </Box>
                  ))}
                  {appRows.length > ROW_CAP ? (
                    <Typography variant="caption" sx={{ color: GREY, p: 1, display: 'block' }}>
                      Showing {ROW_CAP} of {appRows.length} applications — search to narrow the list.
                    </Typography>
                  ) : null}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      ) : null}

      {/* ============================ TAB 3 ============================ */}
      {tab === 2 ? (
        <Box>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}>
              <Kpi label="Servers mapped" value={coverage.mapped}
                sub={servers.length ? `${Math.round((coverage.mapped / servers.length) * 100)}% of ${servers.length}` : 'no servers imported yet'}
                color="#2E7D32" />
            </Grid>
            <Grid item xs={6} md={3}>
              <Kpi label="Orphaned servers" value={coverage.orphans.length}
                sub="no application owns these" color={coverage.orphans.length ? '#C62828' : '#2E7D32'} />
            </Grid>
            <Grid item xs={6} md={3}>
              <Kpi label="Apps with zero servers" value={coverage.zeroApps.length}
                sub={`of ${apps.length} applications`} color={coverage.zeroApps.length ? '#B26A00' : '#2E7D32'} />
            </Grid>
            <Grid item xs={6} md={3}>
              <Kpi label="Estate totals" value={`${coverage.totals.vcpu} vCPU`}
                sub={`${coverage.totals.ram} GB RAM · ${coverage.totals.storage} GB storage`} color={BLUE} />
            </Grid>
          </Grid>

          {coverage.orphans.length ? (
            <Alert
              severity="error" icon={<WarningAmberIcon />} sx={{ mb: 2 }}
              action={<Button size="small" startIcon={<DownloadIcon />} onClick={exportOrphans}>Orphan CSV</Button>}
            >
              <b>{coverage.orphans.length} server(s) belong to no application</b> — {coverage.orphanTotals.vcpu} vCPU,{' '}
              {coverage.orphanTotals.ram} GB RAM and {coverage.orphanTotals.storage} GB of storage that nobody has
              claimed. An unmapped server at cutover is exactly what gets forgotten, missed in a wave, and
              breaks the migration. Work this list to zero before wave planning is signed off.
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mb: 2 }}>
              Every imported server is mapped to an application. That is the state wave planning needs.
            </Alert>
          )}

          <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exportMapping}>
              Mapping CSV
            </Button>
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exportOrphans}>
              Orphan CSV
            </Button>
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exportAppRollup}>
              Per-application roll-up CSV
            </Button>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD, mb: 1 }}>
                  Infrastructure per application
                </Typography>
                <Typography variant="body2" sx={{ color: GREY, mb: 1 }}>
                  Real specs from the estate, not estimates — this is what the cost model should be
                  sized from.
                </Typography>
                <Box sx={{ maxHeight: 460, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={headSx}>
                        <TableCell>Application</TableCell>
                        <TableCell align="right">Servers</TableCell>
                        <TableCell align="right">vCPU</TableCell>
                        <TableCell align="right">RAM (GB)</TableCell>
                        <TableCell align="right">Storage (GB)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {coverage.perApp.slice(0, ROW_CAP).map((r) => (
                        <TableRow key={r.app.id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{r.app.name}</TableCell>
                          <TableCell align="right" sx={{ color: r.count ? 'inherit' : '#B71C1C', fontWeight: r.count ? 400 : 700 }}>
                            {r.count}
                          </TableCell>
                          <TableCell align="right">{r.vcpu || ''}</TableCell>
                          <TableCell align="right">{r.ram || ''}</TableCell>
                          <TableCell align="right">{r.storage || ''}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {coverage.perApp.length > ROW_CAP ? (
                    <Typography variant="caption" sx={{ color: GREY, p: 1, display: 'block' }}>
                      Showing {ROW_CAP} of {coverage.perApp.length} applications — the CSV export has all of them.
                    </Typography>
                  ) : null}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD, mb: 1 }}>
                  Orphaned servers
                </Typography>
                <Box sx={{ maxHeight: 240, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={headSx}>
                        <TableCell>Server</TableCell>
                        <TableCell align="right">vCPU</TableCell>
                        <TableCell align="right">RAM</TableCell>
                        <TableCell>Cluster</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {coverage.orphans.slice(0, ROW_CAP).map((s) => {
                        const f = s.fields || {};
                        return (
                          <TableRow key={s.id} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{s.name}</TableCell>
                            <TableCell align="right">{f.vcpu || ''}</TableCell>
                            <TableCell align="right">{f.ramGb || ''}</TableCell>
                            <TableCell sx={{ color: GREY }}>{f.cluster || ''}</TableCell>
                          </TableRow>
                        );
                      })}
                      {!coverage.orphans.length ? (
                        <TableRow><TableCell colSpan={4} sx={{ color: GREY }}>None — every server is claimed.</TableCell></TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                  {coverage.orphans.length > ROW_CAP ? (
                    <Typography variant="caption" sx={{ color: GREY, p: 1, display: 'block' }}>
                      Showing {ROW_CAP} of {coverage.orphans.length} — export the CSV for the full list.
                    </Typography>
                  ) : null}
                </Box>
              </Paper>

              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD, mb: 1 }}>
                  Applications with no servers
                </Typography>
                <Typography variant="body2" sx={{ color: GREY, mb: 1 }}>
                  Either genuinely SaaS, or the discovery is incomplete. Both answers matter — one is a
                  finding, the other is a gap.
                </Typography>
                <Box sx={{ maxHeight: 220, overflow: 'auto' }}>
                  {coverage.zeroApps.length ? coverage.zeroApps.slice(0, ROW_CAP).map((a) => (
                    <Chip key={a.id} size="small" label={a.name} sx={{ mr: 0.5, mb: 0.5, bgcolor: '#fdecea', color: '#B71C1C', fontWeight: 600 }} />
                  )) : (
                    <Typography variant="body2" sx={{ color: GREY }}>Every application has at least one server.</Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      ) : null}
    </Box>
  );
}
